import React from "react";
import { Card } from "react-bootstrap";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { format } from "date-fns";

const formatPrice = (value: number, minimumFractionDigits = 0): string => {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: minimumFractionDigits,
    }).format(value);
};

interface MonthlyReservationItem {
    month: string;
    name: string;
    total_earning: number;
}

interface SourceItem {
    name: string;
    color: string;
}

interface ReportReservationResponseV4 {
    data: MonthlyReservationItem[];
    total_source: SourceItem[];
}

interface ReportReservationHighChartProps {
    data: ReportReservationResponseV4;
}

const ReportReservationHighChart: React.FC<ReportReservationHighChartProps> = ({ data }) => {

    const pivotedData = data.data.reduce<Record<string, Record<string, number | string>>>((acc, item) => {
        const formattedMonth = format(new Date(`${item.month}-01`), "MMM yyyy");

        if (!acc[formattedMonth]) {
            acc[formattedMonth] = { month: formattedMonth };
        }
        acc[formattedMonth][item.name] = item.total_earning;
        return acc;
    }, {});

    const chartDataValues = Object.values(pivotedData);
    const categories = chartDataValues.map(item => item.month as string);

    const series = data.total_source.map((source): Highcharts.SeriesSplineOptions => ({
        type: 'spline',
        name: source.name,
        color: source.color,
        data: chartDataValues.map(d => (d[source.name] as number) || 0),
        marker: {
            symbol: 'circle'
        }
    }));

    const options: Highcharts.Options = {
        chart: {
            type: 'spline'
        },
        title: {
            text: "Graph of Sales in the Last 12 Months"
        },
        xAxis: {
            categories: categories,
            title: {
                text: "Month"
            },
            crosshair: true
        },
        yAxis: {
            title: {
                text: "Total Earnings"
            },
            labels: {
                formatter: function () {
                    return formatPrice(this.value as number, 0);
                }
            }
        },
        tooltip: {
            shared: true,
            formatter: function () {
                let tooltipText = `<b>${this.x}</b><br/>`;
                this.points?.forEach(point => {
                    // === PERBAIKAN DI SINI ===
                    // Memberikan nilai default 0 jika point.y adalah null atau undefined.
                    // Ini memastikan formatPrice selalu menerima tipe 'number'.
                    const value = point.y ?? 0;
                    tooltipText += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatPrice(value)}</b><br/>`;
                });
                return tooltipText;
            }
        },
        plotOptions: {
            spline: {
                marker: {
                    enabled: true,
                    radius: 4
                }
            }
        },
        series: series,
        credits: {
            enabled: false
        }
    };

    return (
        <Card className="h-100 shadow">
            <Card.Body>
                <HighchartsReact
                    highcharts={Highcharts}
                    options={options}
                />
            </Card.Body>
        </Card>
    );
};

export default ReportReservationHighChart;